// dashboard-servicerequest-base.component.ts
import { Directive, ViewChild } from '@angular/core';
import { TableComponent } from '@shared/components/table/table.component';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { SortParam } from '@app/core/constants/sort-param';
import { HeaderService } from '@app/core/services/header.service';
import { ODataService } from '@app/core/services/odata/odata.service';
import { ModalService } from '@app/core/services/modal.service';
import { Observable } from 'rxjs';
import { SortDir } from '@app/core/constants/sort-dir';
import { serviceRequestTableConfig } from '@shared/table-configs/dashboard/service-request.table';
import { ODataType } from '@app/core/services/odata/types/odata-type';

export interface ServiceRequestConfig {
  tableId: string;
  titleKey: string;
  odataType: (typeof ODataType)[keyof typeof ODataType];
  customParams?: Record<string, string>;
  /** Primary sort applied before any user sort. Omit to use user/default sort only. */
  fixedSort?: SortParam;
  /** Fallback sort used when no user sort is active. Ignored when fixedSort is omitted. */
  defaultSort?: SortParam;
  /** Override the default active columns. Falls back to serviceRequestTableConfig.activeColumns when omitted. */
  activeColumns?: string[];
}

@Directive()
export abstract class DashboardServicerequestBaseComponent {
  @ViewChild('table', { static: false }) public table?: TableComponent<any>;

  protected abstract config: ServiceRequestConfig;
  protected abstract getActions(): MenuAction[];

  public selectedElements: any[] = [];
  public tableConfig!: TableConfig<any>;

  protected constructor(
    protected readonly headerService: HeaderService,
    protected readonly oDataService: ODataService,
    protected readonly modalService: ModalService,
  ) {}

  protected initialize(): void {
    const actions = this.getActions();

    this.tableConfig = {
      id: this.config.tableId,
      title: this.config.titleKey,
      ...serviceRequestTableConfig,
      exportable: true,
      copyable: true,
      floatingHeader: true,
      contextActions: actions,
      serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.search(pageSize, page, order),
      ...(this.config.activeColumns ? { activeColumns: this.config.activeColumns as any } : {}),
    };

    this.headerService.setTitle(this.config.titleKey);
    this.headerService.setActions(actions);
  }

  public search(pageSize: number, page: number, sort?: SortParam): Observable<any> {
    if (this.config.fixedSort) {
      return this.oDataService.dmc.getPaginatedWithFixedSort(
        this.config.odataType,
        { page, pageSize },
        this.config.fixedSort,
        sort,
        this.config.defaultSort,
        undefined,
        this.config.customParams
      );
    }

    return this.oDataService.dmc.getPaginatedWithFixedSort(
      this.config.odataType,
      { page, pageSize },
      sort ?? this.config.defaultSort ?? { sortField: 'occurrence', sortDirection: SortDir.asc },
      undefined,
      undefined,
      undefined,
      this.config.customParams
    );
  }

  public refreshTable(persistSelection: boolean = true): void {
    this.table?.search(persistSelection);
  }

}
